// admin#8 / A-1 — Ops 租戶治理(admin 全域軸)。
// 跨租戶檢視每個 org 的 Ops 產品 entitlement、Plan 承載配額(席次 / 裝置)
// 與觀測用量;開通 / 停用 / 降級走既有「權益贈與」(Plan-grant)商業鏈。
// 與 per-tenant /v3/ops/* 嚴格分離;本面唯讀。

import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { EmptyState, ErrorState, LoadingState } from '../../components/StateBlock';
import { CopyableId } from '../../components/CopyableId';
import '../_shared/shared.css';
import './opsGovernance.css';
import type { OrgOpsGovernanceRow } from './opsGovernanceApi';
import {
  ceilingLabel,
  listOpsGovernanceOrgs,
  orgGrantSubjectLink,
  quotaView,
  stateLabel,
  stateTone,
} from './opsGovernanceApi';

const ORG_STATES = [
  { value: '', label: '全部狀態' },
  { value: 'approved', label: '已核准' },
  { value: 'pending', label: '待審核' },
  { value: 'suspended', label: '已停權' },
];

function QuotaMeter({ used, max, over, unit }: { used: number; max: number; over?: boolean; unit: string }) {
  const view = quotaView(used, max, over);
  return (
    <div className="opsgov-quota" title={`${unit}:${view.label}`}>
      <div className="opsgov-quota-head">
        <span className={`opsgov-quota-value tone-${view.tone === 'neutral' ? 'info' : view.tone}`}>{view.label}</span>
        {view.over && <span className="adm-chip tone-danger">超限</span>}
      </div>
      <div className="opsgov-quota-track">
        {view.ratio == null ? (
          <div className="opsgov-quota-fill opsgov-quota-fill--none" />
        ) : (
          <div
            className={`opsgov-quota-fill opsgov-quota-fill--${view.tone}`}
            style={{ width: `${Math.round(view.ratio * 100)}%` }}
          />
        )}
      </div>
    </div>
  );
}

function OrgRow({
  org,
  active,
  onSelect,
}: {
  org: OrgOpsGovernanceRow;
  active: boolean;
  onSelect: (id: string) => void;
}) {
  return (
    <tr className={active ? 'is-active' : undefined} onClick={() => onSelect(org.org_id)} style={{ cursor: 'pointer' }}>
      <td>
        <div className="adm-cell-strong">{org.name || org.org_id}</div>
        <CopyableId value={org.org_id} />
      </td>
      <td>
        <span className={`adm-chip tone-${stateTone(org.state) === 'neutral' ? 'info' : stateTone(org.state)}`} title={org.state}>
          {stateLabel(org.state)}
        </span>
      </td>
      <td>
        {org.ops_entitled ? (
          <span className="adm-chip tone-good">已開通</span>
        ) : (
          <span className="adm-chip tone-info">未開通</span>
        )}
      </td>
      <td>
        {org.plan ? (
          <div>
            <div className="adm-cell-strong">{org.plan.plan_name || org.plan.plan_id}</div>
            <span className="text-tertiary opsgov-plan-ceilings">
              席次上限 {ceilingLabel(org.plan.max_seats)} · 裝置上限 {ceilingLabel(org.plan.max_devices)}
            </span>
          </div>
        ) : (
          <span className="text-tertiary">—</span>
        )}
      </td>
      <td>
        <QuotaMeter used={org.usage.seats_used} max={org.plan?.max_seats ?? 0} over={org.usage.seats_over_limit} unit="席次" />
      </td>
      <td>
        <QuotaMeter used={org.usage.devices_used} max={org.plan?.max_devices ?? 0} over={org.usage.devices_over_limit} unit="裝置" />
      </td>
      <td className="adm-nowrap text-tertiary">{org.usage.node_count}</td>
    </tr>
  );
}

function OrgDetailPanel({ org }: { org: OrgOpsGovernanceRow | null }) {
  if (!org) {
    return (
      <section className="card opsgov-detail">
        <EmptyState title="選一個組織" detail="點左側任一列以檢視 Ops entitlement、Plan 配額與用量明細。" />
      </section>
    );
  }
  return (
    <section className="card opsgov-detail">
      <div className="section-head">
        <h2 className="section-title">{org.name || org.org_id}</h2>
        <span className={`adm-chip tone-${org.ops_entitled ? 'good' : 'info'}`}>
          {org.ops_entitled ? 'Ops 已開通' : 'Ops 未開通'}
        </span>
      </div>

      <div className="adm-detail-grid">
        <div className="adm-kv">
          <span className="adm-field-label">組織 ID</span>
          <CopyableId value={org.org_id} />
        </div>
        <div className="adm-kv">
          <span className="adm-field-label">狀態</span>
          <span>{stateLabel(org.state)}</span>
        </div>
        {org.owner_user_id && (
          <div className="adm-kv">
            <span className="adm-field-label">擁有者</span>
            <CopyableId value={org.owner_user_id} />
          </div>
        )}
        <div className="adm-kv">
          <span className="adm-field-label">目前方案</span>
          <span>{org.plan ? org.plan.plan_name || org.plan.plan_id : '—'}</span>
        </div>
      </div>

      <div className="opsgov-detail-quotas">
        <div>
          <span className="adm-field-label">席次用量(生效中的 Ops 權益 + 擁有者)</span>
          <QuotaMeter used={org.usage.seats_used} max={org.plan?.max_seats ?? 0} over={org.usage.seats_over_limit} unit="席次" />
        </div>
        <div>
          <span className="adm-field-label">裝置用量(設施掛載裝置)</span>
          <QuotaMeter used={org.usage.devices_used} max={org.plan?.max_devices ?? 0} over={org.usage.devices_over_limit} unit="裝置" />
        </div>
        <div className="adm-kv">
          <span className="adm-field-label">設施節點數</span>
          <span>{org.usage.node_count}</span>
        </div>
      </div>

      <div className="adm-inline-note opsgov-action-note">
        <p>
          Ops 產品的<strong>開通 / 停用 / 降級</strong>沿用既有商業鏈,於「權益贈與」以 subject
          <span className="adm-mono"> org:{org.org_id} </span>建立、撤銷或替換方案權益贈與(不另造產品開關)。
        </p>
        <Link className="btn-ghost btn-sm" to={orgGrantSubjectLink(org.org_id)}>
          前往權益贈與治理 →
        </Link>
      </div>
    </section>
  );
}

export default function OpsGovernancePage() {
  const [state, setState] = useState('');
  const [queryInput, setQueryInput] = useState('');
  const [query, setQuery] = useState('');
  const [entitledOnly, setEntitledOnly] = useState(false);
  const [items, setItems] = useState<OrgOpsGovernanceRow[] | null>(null);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [cursorStack, setCursorStack] = useState<string[]>([]);
  const [currentCursor, setCurrentCursor] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const [selectedId, setSelectedId] = useState<string | null>(null);

  async function load(cursor = '', stack = cursorStack) {
    setLoading(true);
    setError(null);
    try {
      const page = await listOpsGovernanceOrgs({ state, query, cursor, limit: 50, entitledOnly });
      setItems(page.items);
      setNextCursor(page.nextCursor);
      setCurrentCursor(cursor);
      setCursorStack(stack);
      if (page.items.length > 0 && (!selectedId || !page.items.some((o) => o.org_id === selectedId))) {
        setSelectedId(page.items[0].org_id);
      }
      if (page.items.length === 0) setSelectedId(null);
    } catch (e) {
      setError(e);
      setItems(null);
    } finally {
      setLoading(false);
    }
  }

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    void load('', []);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state, entitledOnly]);

  function submitSearch(e: React.FormEvent) {
    e.preventDefault();
    setQuery(queryInput.trim());
  }
  useEffect(() => {
    void load('', []);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query]);

  function clearSearch() {
    setQueryInput('');
    setQuery('');
  }
  function nextPage() {
    if (!nextCursor) return;
    void load(nextCursor, [...cursorStack, currentCursor]);
  }
  function prevPage() {
    if (cursorStack.length === 0) return;
    const stack = [...cursorStack];
    const prev = stack.pop() ?? '';
    void load(prev, stack);
  }

  const kpi = useMemo(() => {
    const list = items ?? [];
    const entitled = list.filter((o) => o.ops_entitled).length;
    const over = list.filter((o) => o.usage.seats_over_limit || o.usage.devices_over_limit).length;
    return { total: list.length, entitled, over };
  }, [items]);

  const selected = useMemo(
    () => (items ?? []).find((o) => o.org_id === selectedId) ?? null,
    [items, selectedId]
  );

  return (
    <>
      <div className="page-header">
        <div>
          <h1 className="page-title">Ops 租戶治理</h1>
          <p className="page-subtitle">
            平台全域(跨租戶)檢視 org 的 Ops 產品開通、Plan 配額(席次 / 裝置)與觀測用量。開通 / 停用 / 降級走「權益贈與」。
          </p>
        </div>
        <button className="btn-ghost btn-sm" onClick={() => void load(currentCursor, cursorStack)} disabled={loading}>
          {loading ? '載入中…' : '重新整理'}
        </button>
      </div>

      {items && (
        <div className="kpi-strip">
          <div className="kpi-card kpi-card--hero">
            <span className="kpi-label">本頁組織</span>
            <span className="kpi-value">
              {kpi.total}
              {nextCursor ? '+' : ''}
            </span>
            <span className="kpi-foot">本頁載入筆數{nextCursor ? '(尚有更多頁)' : ''}</span>
          </div>
          <div className="kpi-card">
            <span className="kpi-label">已開通 Ops</span>
            <span className="kpi-value">{kpi.entitled}</span>
            <span className="kpi-foot">持有生效中的 Ops 權益</span>
          </div>
          <div className="kpi-card kpi-card--secondary">
            <span className="kpi-label">配額超限</span>
            <span className="kpi-value">{kpi.over}</span>
            <span className="kpi-foot">席次或裝置超出方案上限</span>
          </div>
        </div>
      )}

      <div className="toolbar">
        <form className="adm-toolbar-form" onSubmit={submitSearch} role="search">
          <label className="adm-field">
            <span className="text-tertiary adm-field-label">搜尋組織 / slug</span>
            <input
              type="search"
              placeholder="living-style"
              aria-label="搜尋組織"
              value={queryInput}
              onChange={(e) => setQueryInput(e.target.value)}
            />
          </label>
          <button type="submit" className="btn-primary" disabled={loading}>
            查詢
          </button>
          {(query || queryInput) && (
            <button type="button" className="btn-ghost" onClick={clearSearch} disabled={loading}>
              清除
            </button>
          )}
          <span className="adm-toolbar-spacer" />
          <label className="adm-field opsgov-toggle">
            <input
              type="checkbox"
              checked={entitledOnly}
              onChange={(e) => setEntitledOnly(e.target.checked)}
              disabled={loading}
            />
            <span className="text-tertiary adm-field-label">只看已開通</span>
          </label>
          <label className="adm-field">
            <span className="text-tertiary adm-field-label">狀態</span>
            <select value={state} onChange={(e) => setState(e.target.value)} disabled={loading}>
              {ORG_STATES.map((s) => (
                <option key={s.value} value={s.value}>
                  {s.label}
                </option>
              ))}
            </select>
          </label>
        </form>
      </div>

      {loading && !items && (
        <div className="card">
          <LoadingState label="載入 Ops 租戶治理清單…" />
        </div>
      )}

      {error != null && !loading && <ErrorState error={error} onRetry={() => void load(currentCursor, cursorStack)} />}

      {error == null && items && items.length === 0 && (
        <EmptyState title="沒有符合條件的組織" detail="調整狀態、搜尋或「只看已開通」後再查詢。" />
      )}

      {error == null && items && items.length > 0 && (
        <div className="opsgov-layout">
          <section className="card opsgov-directory">
            <div className="section-head">
              <h2 className="section-title">組織目錄</h2>
              <span className="section-meta">
                {query ? `搜尋「${query}」· ` : ''}第 {cursorStack.length + 1} 頁 · 本頁 {items.length} 筆
              </span>
            </div>
            <div className="table-scroll">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>組織</th>
                    <th>狀態</th>
                    <th>Ops</th>
                    <th>方案 / 上限</th>
                    <th>席次用量</th>
                    <th>裝置用量</th>
                    <th>節點</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((org) => (
                    <OrgRow key={org.org_id} org={org} active={org.org_id === selectedId} onSelect={setSelectedId} />
                  ))}
                </tbody>
              </table>
            </div>
            <div className="adm-pager">
              <button className="btn-ghost btn-sm" onClick={prevPage} disabled={cursorStack.length === 0 || loading}>
                ← 上一頁
              </button>
              <span className="text-tertiary">
                第 {cursorStack.length + 1} 頁 · 本頁 {items.length} 筆
              </span>
              <button className="btn-ghost btn-sm" onClick={nextPage} disabled={!nextCursor || loading}>
                下一頁 →
              </button>
            </div>
          </section>

          <OrgDetailPanel org={selected} />
        </div>
      )}
    </>
  );
}
